<template>
    <div v-if="null != courierinfo">
        <v-card>
            <v-card-title v-if="courierinfo.user">
                {{ courierinfo.user.name }}
                <v-spacer />
                <v-chip
                    small
                    label
                    v-bind:color="getChipColor(courierinfo.status)"
                    >{{ courierinfo.status }}</v-chip
                >
            </v-card-title>
            <v-card-subtitle class="d-flex align-center">
                Courier ID: {{ courierinfo.id }}
            </v-card-subtitle>
            <v-divider />
            <v-card-subtitle class="pb-0">
                <v-icon class="mx-1" small>mdi-phone</v-icon>
                {{ courierinfo.user.phone }}
            </v-card-subtitle>
            <v-card-subtitle>
                <v-icon class="mx-1" small>mdi-account</v-icon>
                {{ courierinfo.user.username }}
            </v-card-subtitle>
            <v-divider />
            <v-card-text>Extra details:</v-card-text>
            <v-simple-table dense class="mx-4 pb-4">
                <tbody>
                    <tr>
                        <th class="text--secondary">
                            Date of birth:
                        </th>
                        <td>
                            {{
                                new Date(courierinfo.date_of_birth)
                                    | date('dd-MMM-yyyy')
                            }}
                        </td>
                    </tr>
                    <tr>
                        <th class="text--secondary">
                            Address:
                        </th>
                        <td>{{ courierinfo.address }}</td>
                    </tr>
                    <tr>
                        <th class="text--secondary">
                            Emergency contact:
                        </th>
                        <td>{{ courierinfo.emergency_contact }}</td>
                    </tr>
                    <tr>
                        <th class="text--secondary">
                            Joining date:
                        </th>
                        <td>
                            {{
                                new Date(courierinfo.joining_date)
                                    | date('dd-MMM-yyyy')
                            }}
                        </td>
                    </tr>
                    <tr>
                        <th class="text--secondary">
                            Leaving date:
                        </th>
                        <td>
                            {{
                                new Date(courierinfo.leaving_date)
                                    | date('dd-MMM-yyyy')
                            }}
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
            <v-divider />
            <v-card-actions class="d-flex mx-2">
                <v-spacer />
                <v-btn tile v-on:click="editCourier">
                    <v-icon left>mdi-pencil</v-icon>Edit
                </v-btn>
                <v-btn tile color="error" v-on:click="deleteCourier"
                    ><v-icon left>mdi-delete</v-icon>Delete</v-btn
                >
            </v-card-actions>
        </v-card>
        <courier-cash-details
            class="ma-4"
            v-bind:cashDetails="cashDetails"
            v-bind:courierinfo="courierinfo"
            v-on:request-save-cashtransaction="onRequestSaveCashtransaction"
            v-on:request-delete-cashtransaction="onRequestDeleteCashtransaction"
            v-on:date-change="onDetailsDateChange"
        />
        <courier-servicecharge-details
            class="ma-4"
            v-bind:servicechargeDetails="servicechargeDetails"
            v-on:date-change="onDetailsDateChange"
        />
    </div>
</template>

<script>
import arrayUtil from '@/utils/arrayUtil';
import dateUtil from '@/utils/dateUtil';
import cashDetailsApi from '@/api/cashDetailsApi';
import courierApi from '@/api/courierApi';
import snackbar from '@/utils/snackbar';
import statusUtil from '@/utils/statusUtil';

export default {
    components: {
        'courier-cash-details': () =>
            import('@/components/CourierCashDetails.vue'),
        'courier-servicecharge-details': () =>
            import('@/components/CourierServicechargeDetails.vue')
    },
    data() {
        return {
            courierinfo: null,
            cashDetails: null,
            servicechargeDetails: null
        };
    },
    async beforeRouteEnter(to, from, next) {
        const courierinfoId = to.params.courierinfo_id;
        const response = await courierApi.getCourier(courierinfoId);
        next(vm => vm.setData(response.data));
    },
    methods: {
        setData(data) {
            this.courierinfo = data.courierinfo;
            this.cashDetails = data.cash_details;
            this.servicechargeDetails = data.servicecharge_details;
        },
        editCourier() {
            this.$router.push({
                name: 'op-courierinfo-edit',
                params: {
                    courierinfo_id: this.courierinfo.id
                }
            });
        },
        async deleteCourier() {
            const confirmed = confirm('Confirm deletion');
            if (!confirmed) return;
            await courierApi.deleteCourier(this.courierinfo.id);
            this.$router.push({ name: 'op-courierinfo-list' });
            snackbar.show('Deleted successfully', 'success');
        },
        async onRequestSaveCashtransaction(event) {
            let response = null;
            // Existing id indicates edit, else create new
            if (event.cashtransaction.id != null) {
                // was editing
                response = await cashDetailsApi.updateCashtransaction(
                    event.cashtransaction.id,
                    event.cashtransaction
                );
                snackbar.show(
                    'Updated cash transaction successfully',
                    'success'
                );
            } else {
                // creating new
                let params = event.cashtransaction;

                // Specify selected date if not today
                if (
                    !dateUtil.isDateSame(
                        new Date(),
                        new Date(this.cashDetails.date)
                    )
                ) {
                    let selectedDate = new Date(this.cashDetails.date);
                    selectedDate.setHours(23, 59, 59, 0);
                    params['datetime'] = selectedDate.toLocaleString();
                }

                response = await cashDetailsApi.createCashtransaction(
                    event.courierinfoId,
                    params
                );
                snackbar.show('Added cash transaction successfully', 'success');
            }
            arrayUtil.updateOrCreateById(
                this.cashDetails.cashtransactions,
                response.data.cashtransaction
            );

            // HACK: Force recomputation
            this.cashDetails.cashtransactions = [
                ...this.cashDetails.cashtransactions
            ];
        },
        async onRequestDeleteCashtransaction(cashtransaction) {
            const confirmed = confirm('Confirm deletion');
            if (!confirmed) return;
            await cashDetailsApi.deleteCashtransaction(cashtransaction.id);
            arrayUtil.deleteById(
                this.cashDetails.cashtransactions,
                cashtransaction.id
            );
            snackbar.show('Deleted successfully', 'success');
        },
        async onDetailsDateChange(event) {
            const response = await courierApi.getMiscDetails(
                this.courierinfo.id,
                event
            );
            this.cashDetails = response.data.cash_details;
            this.servicechargeDetails = response.data.servicecharge_details;
        },
        getChipColor(status) {
            return statusUtil.getCourierStatusColor(status);
        }
    }
};
</script>
